import RefundAccountFields, {useRefundAccount} from './RefundAccountFields';
import UserModalHeader from '../../../../Component/UI/UserModalHeader';
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import api from "../../../../Component/axios_Interceptor/api";
import url from "../../../../Component/server";
import { RowView } from "../../../../Component/style/common_style";
import SearchAddrApi from "../../Sign/Modal/SearchAddrApi";
import { useNicknameCheck } from "../../../../Component/tool/useNicknameCheck";

interface MyInfoModalProps {
  visible: boolean;
  onClose: () => void;
  onSaved?: () => void;
  initialSection?: "profile" | "refund";
}

const MyInfoModal: React.FC<MyInfoModalProps> = ({ visible, onClose, onSaved, initialSection = "profile" }) => {
  const refundAccount = useRefundAccount(visible);
  const bodyRef = useRef<HTMLDivElement>(null);
  const refundRef = useRef<HTMLDivElement>(null);
  const scrolled = useRef(false);
  useEffect(() => { if (!visible) scrolled.current = false; }, [visible]);
  useLayoutEffect(() => {
    if (!visible || loading || !refundAccount.ready) { scrolled.current = false; return; }
    if (scrolled.current) return;
    if (initialSection === "refund" && bodyRef.current && refundRef.current) {
      bodyRef.current.scrollTop = refundRef.current.offsetTop - 24;
    }
    scrolled.current = true;
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [nickname, setNickname] = useState("");
  const [originalNickname, setOriginalNickname] = useState("");
  const nicknameCheck = useNicknameCheck(nickname, originalNickname);
  const [addrNum, setAddrNum] = useState("");
  const [addrBasic, setAddrBasic] = useState("");
  const [addrDetail, setAddrDetail] = useState("");

  useEffect(() => {
    if (visible) {
      loadUserInfo();
    }
  }, [visible]);

  const loadUserInfo = async () => {
    setLoading(true);
    try {
      const res = await api.get(url.Communication_url.user_info);
      if (res.data) {
        setName(res.data.name || "");
        setEmail(res.data.user_email || "");
        setPhone(res.data.phone || "");
        setNickname(res.data.nickname || "");
        setOriginalNickname(res.data.nickname || "");
        setAddrNum(res.data.addr_num || "");
        setAddrBasic(res.data.addr_basic || "");
        setAddrDetail(res.data.addr_detail || "");
      }
    } catch (err: any) {
      alert("회원 정보를 불러오지 못했습니다. 로그인 상태를 확인해주세요.");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSearchAddress = () => {
    SearchAddrApi({
      setFirAddr: setAddrBasic,
      setPostCode: setAddrNum,
      setDetail: setAddrDetail,
    });
  };

  const handleSave = async () => {
    if (saving || !nicknameCheck.available) return;
    if (!nickname.trim()) {
      alert("닉네임을 입력해주세요.");
      return;
    }
    setSaving(true);
    try {
      const refund = refundAccount.payload();
      const response = await api.patch(url.Communication_url.update_profile, {
        ...(refund ? {refund_account:refund} : {}),
        nickname: nickname.trim(), name: name.trim(), phone: phone.replace(/[\s-]/g, ""), user_email: email.trim(),
        addr_num: addrNum,
        addr_basic: addrBasic,
        addr_detail: addrDetail,
      });
      if (refund && (!Number.isSafeInteger(response.data?.refund_account?.version) || response.data.refund_account.version <= refund.expected_version)) throw new Error('환불 계좌 저장 결과를 확인하지 못했습니다. 다시 조회해주세요.');
      onSaved?.();
      alert("내 정보가 성공적으로 수정되었습니다.");
      onClose();
    } catch (err: any) {
      alert(err.response?.data?.message || err.message || "정보 수정 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  return (
    <Container onClick={onClose}>
      <ModalBox role="dialog" aria-modal="true" aria-label="내 정보 수정" onClick={(e) => e.stopPropagation()}>
        <UserModalHeader title="내 정보 수정" onClose={onClose} />

        {loading ? (
          <LoadingText>회원 정보를 불러오는 중...</LoadingText>
        ) : (
          <>
          <Body ref={bodyRef} aria-label="내 정보 입력">
            <FormGroup>
              <Label htmlFor="profile-edit-name">이름</Label>
              <TextInput id="profile-edit-name" value={name} maxLength={10} onChange={event => setName(event.target.value)} />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="profile-edit-phone">연락처</Label>
              <TextInput id="profile-edit-phone" type="tel" value={phone} maxLength={16} onChange={event => setPhone(event.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="profile-edit-email">이메일</Label>
              <TextInput id="profile-edit-email" type="email" value={email} maxLength={100} onChange={event => setEmail(event.target.value)} />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="profile-edit-nickname">닉네임</Label>
              <NicknameRow>
              <TextInput
                type="text"
                id="profile-edit-nickname" placeholder="닉네임을 입력해주세요"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                maxLength={10}
                disabled={saving}
              />
              <AddrBtn type="button" onClick={nicknameCheck.check} disabled={saving || nicknameCheck.checking || !nicknameCheck.valid}>
                {nicknameCheck.checking ? "확인 중" : "중복확인"}
              </AddrBtn>
              </NicknameRow>
              {nicknameCheck.message && <NicknameNotice role="status" $error={!nicknameCheck.available}>{nicknameCheck.message}</NicknameNotice>}
            </FormGroup>

            <FormGroup>
              <Label>우편번호 / 배송지 주소</Label>
              <RowView style={{ gap: 8, marginBottom: 6 }}>
                <TextInput
                  type="text"
                  placeholder="우편번호"
                  value={addrNum}
                  readOnly
                  style={{ width: "120px", backgroundColor: "var(--user-surface-raised)" }}
                />
                <AddrBtn type="button" onClick={handleSearchAddress}>
                  주소 검색
                </AddrBtn>
              </RowView>
              <TextInput
                type="text"
                placeholder="기본 주소"
                value={addrBasic}
                readOnly
                style={{ marginBottom: 6, backgroundColor: "var(--user-surface-raised)" }}
              />
              <TextInput
                type="text"
                placeholder="상세 주소를 입력해주세요"
                value={addrDetail}
                onChange={(e) => setAddrDetail(e.target.value)}
                maxLength={100}
              />
            </FormGroup>

            <div ref={refundRef}><RefundAccountFields state={refundAccount} disabled={saving} prefix="statement-refund" /></div>
          </Body>
            <ButtonGroup>
              <CancelBtn type="button" onClick={onClose} disabled={saving}>
                취소
              </CancelBtn>
              <SaveBtn type="button" onClick={handleSave} disabled={saving || !nicknameCheck.available || !refundAccount.ready}>
                {saving ? "저장 중..." : "저장하기"}
              </SaveBtn>
            </ButtonGroup>
          </>
        )}
      </ModalBox>
    </Container>
  );
};

export default MyInfoModal;

const Container = styled.div`
  z-index: 100;
  background-color: rgba(0, 0, 0, 0.6);
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 16px;
  box-sizing: border-box;
`;

const ModalBox = styled.div`
  button:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 2px; }
  box-sizing: border-box;
  width: 100%;
  max-width: 420px;
  background-color: var(--user-surface);
  color: var(--user-text);
  border-radius: 12px;
  overflow: hidden;
  padding: 0;
  max-height: calc(100dvh - 32px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  position: relative;
  padding: 24px;
  > * { flex-shrink: 0; }
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px 0;
  font-size: 14px;
  color: var(--user-text-muted);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const NicknameRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
`;

const NicknameNotice = styled.p<{ $error: boolean }>`
  margin: 6px 0 0;
  color: ${({ $error }) => $error ? 'var(--user-error)' : 'var(--user-success)'};
  font-size: 13px;
`;

const Label = styled.label`
  font-size: 13px;
  font-family: var(--font-Pretendard-Medium);
  color: var(--user-text-muted);
  margin-bottom: 6px;
`;

const TextInput = styled.input`
  background: var(--user-surface);
  color: var(--user-text);
  &::placeholder { color: var(--user-text-muted); }
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  font-size: 14px;
  font-family: var(--font-Pretendard-Regular);
  border: 1px solid var(--user-border);
  border-radius: 6px;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--user-accent);
  }
`;

const AddrBtn = styled.button`
  min-height: 44px;
  padding: 0 14px;
  background-color: var(--user-surface-raised);
  color: var(--user-text);
  border: 1px solid var(--user-border);
  border-radius: 6px;
  font-size: 13px;
  font-family: var(--font-Pretendard-Medium);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: var(--user-accent-soft);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 0;
  padding: 16px 24px max(16px, env(safe-area-inset-bottom));
  border-top: 1px solid var(--user-border);
  background: var(--user-surface);
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 12px;
  background-color: var(--user-surface-raised);
  color: var(--user-text);
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-family: var(--font-Pretendard-Medium);
  cursor: pointer;

  &:hover {
    background-color: var(--user-accent-soft);
  }
`;

const SaveBtn = styled.button`
  flex: 2;
  padding: 12px;
  background-color: var(--user-button);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-family: var(--font-Pretendard-SemiBold);
  cursor: pointer;

  &:hover {
    background-color: #4f2bb8;
  }

  &:disabled {
    background-color: var(--user-surface-raised);
    color: var(--user-text-muted);
    cursor: not-allowed;
  }
`;
